import type {
  ComponentStatus,
  LatencySeries,
  StatusComponent,
  StatusSnapshot,
  UptimeBand,
} from '@/types';
import { statusHeadlines, statusLabels, statusMessages, uptimeBandLabels } from '@/i18n';

const MINUTES_PER_DAY = 1440;

/**
 * Band thresholds for a day cell.
 *
 * Deliberately finer-grained than `ComponentStatus`: most real degradations sit
 * between 95% and 99.9%, which a binary up/down bar would paint solid green.
 */
export function uptimeBand(uptime: number | null | undefined): UptimeBand {
  if (uptime === null || uptime === undefined || Number.isNaN(uptime)) return 'nodata';
  if (uptime >= 0.999) return 'ok';
  if (uptime >= 0.99) return 'nominal';
  if (uptime >= 0.95) return 'degraded';
  if (uptime >= 0.7) return 'partial';
  return 'major';
}

/** Band for a day, falling back to the coarse status on pre-uptime snapshots. */
export function dayBand(uptime: number | null | undefined, status: ComponentStatus): UptimeBand {
  if (uptime !== null && uptime !== undefined) return uptimeBand(uptime);
  switch (status) {
    case 'operational':
      return 'ok';
    case 'degraded':
    case 'maintenance':
      return 'degraded';
    case 'partial_outage':
      return 'partial';
    case 'major_outage':
      return 'major';
    default:
      return 'nodata';
  }
}

export function uptimeBandLabel(band: UptimeBand): string {
  return uptimeBandLabels[band];
}

export function uptimeBandClass(band: UptimeBand): string {
  return `band-${band}`;
}

/** Reads the current field, tolerating a snapshot written by the previous collector. */
export function componentUptime(component: StatusComponent): number | null {
  if (component.uptime !== null && component.uptime !== undefined) return component.uptime;
  return component.uptime90d ?? null;
}

/**
 * Approximate minutes of downtime implied by a day's uptime ratio.
 *
 * The uptime check runs once a minute, so the ratio maps directly onto minutes.
 * The current day is only partially elapsed, so it is scaled accordingly.
 */
export function downtimeMinutes(uptime: number, date: string, now: Date = new Date()): number {
  const todayKey = now.toISOString().slice(0, 10);
  const elapsed =
    date === todayKey
      ? Math.max(1, Math.round((now.getTime() - Date.parse(`${date}T00:00:00Z`)) / 60000))
      : MINUTES_PER_DAY;
  return Math.round((1 - uptime) * elapsed);
}

export function statusLabel(status: ComponentStatus): string {
  return statusLabels[status];
}

export function statusHeadline(status: ComponentStatus): string {
  return statusHeadlines[status];
}

export function statusToneClass(status: ComponentStatus): string {
  switch (status) {
    case 'operational':
      return 'tone-ok';
    case 'degraded':
    case 'maintenance':
      return 'tone-warn';
    case 'partial_outage':
      return 'tone-warn-strong';
    case 'major_outage':
      return 'tone-bad';
    case 'unknown':
    default:
      return 'tone-muted';
  }
}

/** Formats an uptime ratio, distinguishing "no data" from a genuine 0%. */
export function formatUptime(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}

/**
 * A snapshot older than this was not refreshed by the collector (WOR-221: a paused region keeps
 * serving its last status.json). The browser's own fetch time says nothing about that.
 */
export const STALE_SNAPSHOT_MS = 30 * 60 * 1000;

export function isSnapshotStale(generatedAt: string, now: Date = new Date()): boolean {
  const generated = Date.parse(generatedAt);
  return Number.isNaN(generated) || now.getTime() - generated > STALE_SNAPSHOT_MS;
}

export function formatRelativeTime(date: Date | null): string {
  if (date === null) return '—';
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return statusMessages.relativeJustNow;
  if (seconds < 60) return statusMessages.relativeSecondsAgo(seconds);
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return statusMessages.relativeMinutesAgo(minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return statusMessages.relativeHoursAgo(hours);
  const days = Math.floor(hours / 24);
  return statusMessages.relativeDaysAgo(days);
}

export function rollupGroupStatus(snapshot: StatusSnapshot, groupId: string): ComponentStatus {
  const group = snapshot.groups.find((g) => g.id === groupId);
  if (!group) return 'unknown';
  const severities: ComponentStatus[] = [
    'major_outage',
    'partial_outage',
    'degraded',
    'maintenance',
    'unknown',
    'operational',
  ];
  for (const sev of severities) {
    if (group.components.some((c) => c.status === sev)) return sev;
  }
  return 'operational';
}

/**
 * Whether a series holds at least one measurement.
 *
 * The collector omits the field entirely rather than emitting an all-null
 * series, but the frontend renders whatever `status.json` contains: without
 * this guard an all-null series would draw an axis of five "0 ms" gridlines
 * and an empty plot, which reads as a measured zero rather than as no data.
 */
export function hasLatencyData(series: LatencySeries | null | undefined): series is LatencySeries {
  return series !== null && series !== undefined && series.values.some((v) => v !== null);
}

/** Formats a latency in milliseconds, distinguishing "no data" from a real 0. */
export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${Math.round(ms)} ms`;
}

/**
 * A round step for a latency axis, at or above `rough`.
 *
 * Picking the step (not the ceiling) is what keeps every gridline on a readable
 * number: a ceiling snapped to the data would leave labels like 8333 ms between
 * 0 and the top.
 */
export function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  for (const multiple of [1, 2, 2.5, 5, 10]) {
    const step = multiple * magnitude;
    if (step >= rough) return step;
  }
  return 10 * magnitude;
}

/**
 * Gridline values for the response-time axis, from 0 up to a round ceiling.
 *
 * The axis is anchored at zero on purpose. A latency chart auto-zoomed to its
 * own min/max turns a 91–95 ms service into a dramatic mountain range; against
 * a zero baseline, flat reads as flat, which is the honest picture.
 */
export function latencyTicks(maxMs: number, divisions = 4): number[] {
  const safeMax = Number.isFinite(maxMs) && maxMs > 0 ? maxMs : 1;
  const step = niceStep(safeMax / divisions);
  return Array.from({ length: divisions + 1 }, (_, index) => step * index);
}
